import { sizeFormatter } from 'human-readable';
import { v4 as uuidv4 } from 'uuid';
import * as Browser from '../../../browser';
import * as Validate from './validate';

export default (params) => ({
    uploading: params.uploading ?? false,
    multiple: params.multiple ?? false,
    defaultThumbnail: params.defaultThumbnail,
    rules: params.rules ?? '',
    componentId: params.componentId,
    single: params.single ?? false,
    messages: params.messages,
    namespace: params.namespace,
    sendToTemporary: params.sendToTemporary,
    uploadProgress: 0,
    files: null,
    downloadStarted: false,
    filesBag: [],
    abort: false,
    source: null,
    tmpImage: '',
    storePermanently: false,
    validationError: false,
    fileFromOneDrive: false,
    widgetState: true,
    showProgressWidget: false,
    uploadBatchId: null,
    init() {
        this.$watch('files', (value) => {
            if (value && value.length > 0) {
                this.defaultThumbnail = '';
                this.$dispatch('upload-started::' + this.componentId);
                this.startUpload();
                this.storePermanently = false;
            }
        });

        this.$watch('storePermanently', (value) => {
            if (value && this.files) {
                this.sendToTemporary = false;
                this.namespace = params.namespace
                this.startUpload();
            }
        });

        this.$watch('uploading', (value) => this.$wire.set('uploading', value))

        window.addEventListener('upload::cancel', ({ detail }) => {
            if (detail.componentId === this.componentId && detail.batchId === this.uploadBatchId) {
                this.cancel(detail.index);
            }
        });
    },
    get uploadingFiles() {
        return this.filesBag.filter(file => file.completed === false).length
    },
    reset() {
        this.uploading = false;
        this.tmpImage = '';
        this.defaultThumbnail = '';
        this.filesBag = [];
    },
    clearInput() {
        this.files = null
        this.fileFromOneDrive = false;
        if (this.$refs?.file) {
            this.$refs.file.setAttribute('value', '');
        }
        if (this.$refs?.form) {
            this.$refs.form.reset();
        }
    },
    resetThumbnail(key) {
        if (!key || document.getElementById(this.componentId) === null) {
            this.$wire.call('thumbnailUrl', key).then((tmpUrl) => {
                document.getElementById(this.componentId).src = tmpUrl
            });
            return;
        }

        this.$wire.call('originalUrlFile', key).then((tmpUrl) => {
            document.getElementById(this.componentId).src = tmpUrl
        });
    },
    startUpload() {
        this.reset();
        this.uploadBatchId = uuidv4();
        this.uploading = true
        this.widgetState = true
        this.showProgressWidget = true

        if (this.sendToTemporary) {
            this.namespace = 'tmp'
        }

        if (this.multiple && !this.fileFromOneDrive) {
            this.prepareMultipleUpload();
            this.processUpload();

            return;
        }

        this.prepareSingleUpload();
    },
    prepareSingleUpload() {
        const defaults = sizeFormatter();

        const file = this.files[0];

        const validation = Validate.make(this.rules, file);

        this.$wire.call('getValidationMessages', validation).then(response => {
            if (response.length > 0) {
                this.validationError = true

                const id = Math.random().toString(16).slice(2)
                window.addEventListener('confirmed-' + id, () => this.reset());

                this.$dispatch('confirm', {
                    description: response,
                    title: 'Error while uploading',
                    type: 'warning',
                    confirmLabel: 'Ok',
                    showCancelButton: false,
                    id: id
                })

                return;
            }

            this.filesBag.push({
                file,
                human_size_readable: defaults(file.size),
                progress: 0,
                downloading: false,
                completed: false,
                error: false,
            });

            window.dispatchEvent(new CustomEvent('upload::started', {
                detail: {
                    componentId: this.componentId,
                    batchId: this.uploadBatchId,
                    files: [{
                        name: file.name,
                        humanSize: defaults(file.size),
                        progress: 0,
                        completed: false,
                        abort: false,
                        error: false,
                        index: 0,
                    }],
                }
            }));

            this.processUpload();
        })
    },
    prepareMultipleUpload() {
        const defaults = sizeFormatter();

        const files = this.files;

        for (let index = 0; index < files.length; index++) {
            this.filesBag.push({
                file: files[index],
                human_size_readable: defaults(files[index].size),
                progress: 0,
                downloading: false,
                completed: false,
                abort: false,
                error: false,
                vapor: [],
            });
        }

        window.dispatchEvent(new CustomEvent('upload::started', {
            detail: {
                componentId: this.componentId,
                batchId: this.uploadBatchId,
                files: this.filesBag.map((f, idx) => ({
                    name: f.file.name,
                    humanSize: f.human_size_readable,
                    progress: 0,
                    completed: false,
                    abort: false,
                    error: false,
                    index: idx,
                })),
            }
        }));
    },
    processUpload() {
        const self = this;

        (async function (next) {
            for (let index = 0; index < self.filesBag.length; index++) {
                self.abort = false;
                const uuid = uuidv4();
                const file = self.filesBag[index].file;

                const key = self.namespace + '/' + uuid;

                try {
                    await self.uploadVapor(index, file, key).then(async (response) => {
                        if (typeof response === "undefined") {
                            return;
                        }

                        response.uuid = uuid;
                        response.key = key
                        response.file = file;

                        const element = document.getElementById(self.componentId);

                        if (self.multiple || self.storePermanently || self.single) {
                            await self.store(response);

                            if (self.storePermanently || self.single) {
                                await self.$wire.call('originalUrlFile', response.key).then((tmpUrl) => {
                                    if (element != null) {
                                        element.src = tmpUrl
                                    }
                                });
                            }
                        }

                        if (!self.storePermanently && !self.multiple && !self.single) {
                            await self.$wire.call('originalUrlFile', response.key).then((tmpUrl) => {
                                self.tmpImage = tmpUrl;
                                if (element != null) {
                                    element.src = tmpUrl
                                }
                            });
                        }
                        self.filesBag[index].vapor = response;
                        self.filesBag[index].completed = true;
                        self.filesBag[index].downloading = false;

                        window.dispatchEvent(new CustomEvent('upload::completed', {
                            detail: { componentId: self.componentId, batchId: self.uploadBatchId, index: index }
                        }));
                    });
                } catch (error) {
                    if (self.filesBag.length === 0) {
                        return;
                    }

                    self.filesBag[index].progress = 0;
                    self.filesBag[index].error = true;
                    self.filesBag[index].downloading = false;

                    let err;

                    if (self.abort) {
                        err = self.messages['cancelMessage'];
                        self.filesBag[index].completed = true;
                    } else {
                        err = self.messages['failedUploadMessage'];
                    }

                    self.notify('error', err);

                    window.dispatchEvent(new CustomEvent('upload::failed', {
                        detail: { componentId: self.componentId, batchId: self.uploadBatchId, index: index, abort: self.abort }
                    }));
                }
                self.source = null;
            }
            next();
        }(async () => {
            if (self.abort && (!Browser.isFirefox() || !Browser.isSafari())) {
                return;
            }

            if (self.multiple || self.single) {
                self.files = null;
                if (self.$refs?.file) {
                    self.$refs.file.setAttribute('value', '');
                }
            }

            self.clearInput();

            self.uploading = false

            self.$wire.dispatch('attachment::refresh')

            let temporaryUrl = ''

            if (self.single && self.filesBag[0].completed) {
                await self.$wire.call('originalUrlFile', self.filesBag[0].vapor.key).then((url) => {
                    temporaryUrl = url
                });
                self.$dispatch('upload-finished::' + this.componentId, {
                    files: self.filesBag,
                    temporaryUrl: temporaryUrl,
                    key: self.filesBag[0].vapor.key,
                });
            }
        }));
    },
    cancelAll() {
        if (Browser.isFirefox()) {
            window.location.reload();
        }

        if (this.uploading) {
            window.location.reload()

            return
        }

        this.filesBag.map((file) => {
            if (file.downloading) {
                file.abort = true
                file.completed = true
                file.downloading = false
            }
            return file
        })

        this.uploading = false
        this.widgetState = false
        this.showProgressWidget = false

        const hasAborted = this.filesBag.filter(file => file.abort).length > 0

        if (hasAborted && this.source) {
            this.source.cancel(this.messages['cancelMessage']);
        }

        this.$wire.dispatch('attachment::refresh')

    },
    cancel(index) {
        if (!this.filesBag || !this.filesBag[index]) {
            return;
        }
        this.abort = true;
        this.filesBag[index].completed = true;
        this.filesBag[index].abort = true;

        if (this.filesBag[index].downloading && this.source) {
            this.source.cancel(this.messages['cancelMessage']);
        }

        const totalCompleted = this.filesBag.filter((file) => file.completed === true).length;

        if (totalCompleted === this.filesBag.length) {
            if (Browser.isFirefox()) {
                window.location.reload();
            }

            this.uploading = false;
        }
    },
    store(response) {
        return this.$wire.store({
            uuid: response.uuid,
            key: response.key,
            extension: response.extension,
            bucket: response.bucket,
            name: response.file.name,
            mimeType: response.file.type,
            size: response.file.size
        });
    },
    async uploadVapor(index, file, key) {
        try {
            this.source = window.axios.CancelToken.source();
            this.filesBag[index].downloading = true;

            if (this.filesBag[index].abort) {
                return;
            }

            return await VaporS3.store(file, {
                progress: (progress) => {
                    const percent = Math.round(progress * 100);
                    if (typeof this.filesBag !== "undefined" && this.filesBag[index]) {
                        this.filesBag[index].progress = percent;
                    }
                    window.dispatchEvent(new CustomEvent('upload::progress', {
                        detail: { componentId: this.componentId, batchId: this.uploadBatchId, index: index, progress: percent }
                    }));
                },
                data: { key },
                cancelToken: this.source.token,
            });
        } catch (error) {
            throw new Error(error);
        }
    },
    notify(type, message) {
        window.dispatchEvent(new CustomEvent('notify', {
            detail: { type: type, title: message },
        }));
    },
    fetchFromOneDrive(file, name) {
        axios.get(file, {
            onDownloadProgress: progressEvent => {
                let percentCompleted = Math.floor(progressEvent.loaded / progressEvent.total * 100)

                this.notify('info', "Fetching file from OneDrive: " + percentCompleted + "%")
            },
            responseType: 'blob',
        })
            .then(async response => {
                const file = new File([response.data], name, { type: response.data.type });

                this.files = [file];

                this.fileFromOneDrive = true;
            });
    }
});
